import { LRUMap } from 'lru_map';

/**
 * Builds a LRU cache instance
 *
 * @param maxEntries - The maximum number of entries to store in the cache
 */
export const buildLRUCache = <Key = unknown, Value = unknown>(
  maxEntries: number
) => {
  const instance = new LRUMap<Key, Value>(maxEntries);

  const init = async (values: Entry<Key, Value>[]) => {
    if (!values) {
      return;
    }

    instance.assign(values.map(item => [item.key, item.value]));
  };

  const get = (key: Key): Value | undefined => {
    return instance.get(key);
  };

  const set = (
    key: Key,
    value: Value,
    updateCacheCallback: (lru: LRUMap<Key, Value>) => void = () => undefined
  ) => {
    const result = instance.set(key, value);
    updateCacheCallback(instance);
    return result;
  };

  const clear = (
    updateCacheCallback: (lru: LRUMap<Key, Value>) => void = () => undefined
  ) => {
    instance.clear();
    updateCacheCallback(instance);
  };

  const deleteByKey = (key: Key) => instance.delete(key);

  const toKeyValueArrays = () => {
    const keys: Key[] = [];
    const values: Value[] = [];

    instance.forEach((value, key) => {
      keys.push(key);
      values.push(value);
    });

    return { keys, values };
  };

  const size = () => instance.size;

  const toJSON = () => instance.toJSON();

  return {
    /**
     * Initializes the cache with the given values
     */
    init,
    /**
     * Gets the value for the given key
     */
    get,
    /**
     * Sets the value for the given key
     * @param key - The key to set
     * @param value - The value to set
     * @param updateCacheCallback - Optional callback to execute after setting
     */
    set,
    /**
     * Clears the cache
     * @param updateCacheCallback - Optional callback to execute after clearing
     */
    clear,
    /**
     * Deletes the value for the given key
     * @param key - The key to delete
     */
    deleteByKey,
    /**
     * Returns the entries in the cache as separate keys and values arrays
     */
    toKeyValueArrays,
    /**
     * Returns the current size of the cache
     */
    size,
    /**
     * Returns the cache as a JSON object
     */
    toJSON,
  } as const;
};
export interface Entry<K = unknown, V = unknown> {
  key: K;
  value: V;
}
